import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { acceptInvitation } from '@/lib/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link.');
      return;
    }
    acceptInvitation(token)
      .then((data) => {
        setStatus('success');
        setMessage(data.message ?? 'Thank you for accepting the invitation.');
        // After verifying the invitation, take them to courses.
        setTimeout(() => navigate('/courses'), 900);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Something went wrong.');
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-accent animate-spin mx-auto mb-6" />
            <p className="text-muted-foreground">Accepting your invitation…</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-africa-green mx-auto mb-6" />
            <h1 className="text-2xl font-display font-bold text-foreground mb-2">
              You're in
            </h1>
            <p className="text-muted-foreground mb-8">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/courses"
                className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-6 py-3 font-medium hover:bg-primary/90"
              >
                Explore Programs
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-lg border border-border px-6 py-3 font-medium hover:bg-secondary"
              >
                Return home
              </Link>
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
            <h1 className="text-2xl font-display font-bold text-foreground mb-2">
              Invitation could not be accepted
            </h1>
            <p className="text-muted-foreground mb-8">{message}</p>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-lg border border-border px-6 py-3 font-medium hover:bg-secondary"
            >
              Return home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
